import { ReactNode } from 'react'

import {
  ChevronFirst,
  ChevronLast,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react'

import { Button } from '~/components/component-library'
import { formatWholeNumber } from '~/utils/numbers'
import { cn } from '~/utils/tailwind-utils'

/**
 * Props for numbered (page-based) pagination.
 * Supports first/last page navigation since total count is known.
 */
type NumberedPageNavigatorProps = {
  mode: 'numbered'
  pageCountStart: number
  pageCountEnd: number
  pageCountTotal: number
  canGoPreviousPage: boolean
  canGoNextPage: boolean
  goToPreviousPage: () => void
  goToNextPage: () => void
  includeFirstAndLastPage?: boolean
  goToFirstPage: () => void
  goToLastPage: () => void
  trailing?: ReactNode
  className?: string
}

/**
 * Props for cursor-based pagination.
 * Does not support goToLastPage since total count is unknown with cursor pagination.
 * Only supports navigating to first page (reset cursor) and sequential prev/next navigation.
 */
type CursorPageNavigatorProps = {
  mode: 'cursor'
  canGoFirstPage: boolean
  canGoPreviousPage: boolean
  canGoNextPage: boolean
  goToFirstPage: () => void
  goToPreviousPage: () => void
  goToNextPage: () => void
  className?: string
}

type PageNavigatorProps = NumberedPageNavigatorProps | CursorPageNavigatorProps

export const PageNavigator = (props: PageNavigatorProps) => {
  if (props.mode === 'cursor') {
    const {
      canGoFirstPage,
      canGoPreviousPage,
      canGoNextPage,
      goToFirstPage,
      goToPreviousPage,
      goToNextPage,
      className,
    } = props

    return (
      <div
        className={cn('flex items-center justify-end gap-1 text-sm', className)}
      >
        <div className="flex items-center">
          <Button
            variant="tertiary"
            size="small"
            onClick={goToFirstPage}
            isDisabled={!canGoFirstPage}
          >
            <ChevronFirst className="w-4" />
          </Button>
          <Button
            variant="tertiary"
            size="small"
            onClick={goToPreviousPage}
            isDisabled={!canGoPreviousPage}
          >
            <ChevronLeftIcon className="w-4" />
          </Button>
        </div>
        <div className="flex items-center">
          <Button
            variant="tertiary"
            size="small"
            onClick={goToNextPage}
            isDisabled={!canGoNextPage}
          >
            <ChevronRightIcon className="w-4" />
          </Button>
        </div>
      </div>
    )
  }

  const {
    pageCountStart,
    pageCountEnd,
    pageCountTotal,
    canGoPreviousPage,
    canGoNextPage,
    goToPreviousPage,
    goToNextPage,
    includeFirstAndLastPage = false,
    goToFirstPage,
    goToLastPage,
    trailing,
    className,
  } = props

  return (
    <div
      className={cn('flex items-center justify-end gap-1 text-sm', className)}
    >
      <div className="flex items-center">
        {includeFirstAndLastPage && (
          <Button
            variant="tertiary"
            size="small"
            onClick={goToFirstPage}
            isDisabled={!canGoPreviousPage}
          >
            <ChevronFirst className="w-4" />
          </Button>
        )}
        <Button
          variant="tertiary"
          size="small"
          onClick={goToPreviousPage}
          isDisabled={!canGoPreviousPage}
        >
          <ChevronLeftIcon className="w-4" />
        </Button>
      </div>
      <div className="flex px-1">
        {formatWholeNumber(pageCountStart)}-{formatWholeNumber(pageCountEnd)} of{' '}
        {formatWholeNumber(pageCountTotal)}
      </div>
      <div className="flex items-center">
        <Button
          variant="tertiary"
          size="small"
          onClick={goToNextPage}
          isDisabled={!canGoNextPage}
        >
          <ChevronRightIcon className="w-4" />
        </Button>
        {includeFirstAndLastPage && (
          <Button
            variant="tertiary"
            size="small"
            onClick={goToLastPage}
            isDisabled={!canGoNextPage}
          >
            <ChevronLast className="w-4" />
          </Button>
        )}
      </div>
      {trailing ? <div className="pl-6">{trailing}</div> : null}
    </div>
  )
}
